import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  list,
  invite,
  resendInvite,
  softDelete,
  replaceProjects,
  listPickableProjects,
  type ExternalUsersListResponse,
  type InviteInput,
  type ReplaceProjectsResponse,
  type PickableProject,
} from '../services/api/externalUsers';

/**
 * TanStack Query hooks for the admin External Users surface.
 *
 * All mutations invalidate the `['external-users']` query key on success
 * (server-confirm only — no optimistic updates, mirroring useAppAccess.ts).
 */

export function useExternalUsers(page: number, perPage: number) {
  return useQuery<ExternalUsersListResponse>({
    queryKey: ['external-users', { page, perPage }],
    queryFn: () => list(page, perPage),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useInviteExternalUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteInput) => invite(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => resendInvite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
    },
  });
}

export function useSoftDeleteExternalUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      // F9-003: soft-deleting an external user revokes their project
      // memberships server-side. Invalidate the whole per-project members
      // namespace because we don't know which projects were affected.
      queryClient.invalidateQueries({ queryKey: ['project-external-members'] });
    },
  });
}

export function useReplaceExternalUserProjects() {
  const queryClient = useQueryClient();
  return useMutation<ReplaceProjectsResponse, Error, { id: number; projectIds: number[] }>({
    mutationFn: ({ id, projectIds }) => replaceProjects(id, projectIds),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['external-users'] });
      // F9-003: invalidate per-project external-members caches for the union
      // of added + removed project IDs so the project detail page reflects
      // the new membership immediately (no ghost rows / no missing entries).
      const affected = new Set<number>([
        ...(response?.added ?? []),
        ...(response?.removed ?? []),
      ]);
      for (const projectId of affected) {
        queryClient.invalidateQueries({
          queryKey: ['project-external-members', projectId],
        });
      }
    },
  });
}

export function usePickableProjects() {
  return useQuery<PickableProject[]>({
    queryKey: ['external-users-pickable-projects'],
    queryFn: () => listPickableProjects(),
    staleTime: 60 * 1000,
  });
}
